/**
 * Component Styles
 * Reusable component-level styles using theme constants
 */

import type { CSSProperties } from "react";
import { BORDER_RADIUS, COLORS, SPACING } from "../constants/theme";

export const SELECT_CHEVRON_STYLE: Pick<
  CSSProperties,
  "appearance" | "backgroundImage" | "backgroundRepeat" | "backgroundPosition"
> = {
  appearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
};

// ============================================================================
// CARD STYLES
// ============================================================================

/**
 * Standard card style used across the application
 */
export const CARD_STYLE: CSSProperties = {
  backgroundColor: COLORS.background.primary,
  border: `1px solid ${COLORS.border.light}`,
  borderRadius: BORDER_RADIUS.lg,
  padding: SPACING.xl,
};

/**
 * Card header style
 */
export const CARD_HEADER_STYLE: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "start",
  marginBottom: SPACING.lg,
};

/**
 * Card content area style
 */
export const CARD_CONTENT_STYLE: CSSProperties = {
  fontSize: "14px",
  color: COLORS.text.secondary,
};

// ============================================================================
// BADGE STYLES
// ============================================================================

/**
 * Base badge style (apply this + specific variant)
 */
export const BADGE_BASE_STYLE: CSSProperties = {
  padding: "4px 12px",
  borderRadius: BORDER_RADIUS.xl,
  fontSize: "12px",
  fontWeight: "600",
  display: "inline-block",
};

/**
 * Assignment status badge styles
 */
export const ASSIGNMENT_STATUS_BADGES = {
  unassigned: {
    backgroundColor: COLORS.warning.light,
    color: COLORS.warning.dark,
    label: "Unassigned",
  },
  assigned_to_manager: {
    backgroundColor: COLORS.info.light,
    color: COLORS.info.dark,
    label: "Assigned to Manager",
  },
  assigned_to_staff: {
    backgroundColor: COLORS.success.light,
    color: COLORS.success.dark,
    label: "Assigned to Staff",
  },
} as const;

/**
 * One-time job status badge styles
 */
export const ONE_TIME_JOB_STATUS_BADGES = {
  scheduled: {
    backgroundColor: COLORS.info.light,
    color: COLORS.info.dark,
  },
  active: {
    backgroundColor: COLORS.success.light,
    color: COLORS.success.dark,
  },
  completed: {
    backgroundColor: COLORS.gray[100],
    color: COLORS.gray[700],
  },
  cancelled: {
    backgroundColor: COLORS.danger.light,
    color: COLORS.danger.dark,
  },
} as const;

// ============================================================================
// BUTTON STYLES
// ============================================================================

/**
 * Button base style
 */
export const BUTTON_BASE_STYLE: CSSProperties = {
  padding: "8px 16px",
  fontSize: "13px",
  fontWeight: "600",
  border: "none",
  borderRadius: BORDER_RADIUS.md,
  cursor: "pointer",
  transition: "all 0.2s",
};

/**
 * Button variants
 */
export const BUTTON_VARIANTS = {
  primary: {
    color: COLORS.text.inverse,
    backgroundColor: COLORS.primary.main,
  },
  success: {
    color: COLORS.success.dark,
    backgroundColor: COLORS.success.light,
  },
  danger: {
    color: COLORS.danger.hover,
    backgroundColor: COLORS.danger.light,
  },
  warning: {
    color: COLORS.warning.dark,
    backgroundColor: COLORS.warning.light,
  },
  info: {
    color: COLORS.info.dark,
    backgroundColor: COLORS.info.light,
  },
  secondary: {
    color: COLORS.text.secondary,
    backgroundColor: COLORS.background.secondary,
  },
} as const;

// ============================================================================
// ALERT/MESSAGE BOX STYLES
// ============================================================================

/**
 * Alert box base style
 */
export const ALERT_BASE_STYLE: CSSProperties = {
  padding: SPACING.md,
  borderRadius: BORDER_RADIUS.md,
  fontSize: "14px",
  marginBottom: SPACING.lg,
};

/**
 * Alert box variants
 */
export const ALERT_VARIANTS = {
  info: {
    backgroundColor: COLORS.info.light,
    color: COLORS.info.dark,
    border: `1px solid ${COLORS.info.main}`,
  },
  success: {
    backgroundColor: COLORS.success.light,
    color: COLORS.success.dark,
    border: `1px solid ${COLORS.success.main}`,
  },
  warning: {
    backgroundColor: COLORS.warning.light,
    color: COLORS.warning.dark,
    border: `1px solid ${COLORS.warning.main}`,
  },
  danger: {
    backgroundColor: COLORS.danger.light,
    color: COLORS.danger.dark,
    border: `1px solid ${COLORS.danger.main}`,
  },
} as const;

// ============================================================================
// EMPTY STATE STYLES
// ============================================================================

/**
 * Empty state container
 */
export const EMPTY_STATE_STYLE: CSSProperties = {
  padding: "40px",
  textAlign: "center",
  backgroundColor: COLORS.background.secondary,
  borderRadius: BORDER_RADIUS.lg,
  color: COLORS.text.secondary,
};

// ============================================================================
// FORM ELEMENTS
// ============================================================================

/**
 * Select/dropdown style
 */
export const SELECT_STYLE: CSSProperties = {
  padding: "8px 36px 8px 12px",
  fontSize: "13px",
  borderRadius: BORDER_RADIUS.md,
  border: `1px solid ${COLORS.border.medium}`,
  backgroundColor: COLORS.background.primary,
  cursor: "pointer",
  ...SELECT_CHEVRON_STYLE,
};

/**
 * Helper function to create badge style object
 */
export const createBadgeStyle = (
  backgroundColor: string,
  color: string,
  additionalStyles?: CSSProperties,
): CSSProperties => ({
  ...BADGE_BASE_STYLE,
  backgroundColor,
  color,
  ...additionalStyles,
});

/**
 * Helper function to create button style object
 */
export const createButtonStyle = (
  variant: keyof typeof BUTTON_VARIANTS,
  additionalStyles?: CSSProperties,
): CSSProperties => ({
  ...BUTTON_BASE_STYLE,
  ...BUTTON_VARIANTS[variant],
  ...additionalStyles,
});
